import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Get user's collection reference
function getUserCollection(userId, collectionName) {
  return collection(db, 'users', userId, collectionName);
}

// Get user's document reference
function getUserDoc(userId, collectionName, docId) {
  return doc(db, 'users', userId, collectionName, docId);
}

// ==================== CLIENTS ====================

export async function saveClientToFirestore(userId, client) {
  try {
    const clientRef = getUserDoc(userId, 'clients', client.id);
    await setDoc(clientRef, {
      ...client,
      updatedAt: new Date().toISOString()
    });
    return client;
  } catch (error) {
    console.error('Error saving client:', error);
    throw error;
  }
}

export async function getClientsFromFirestore(userId) {
  try {
    const clientsRef = getUserCollection(userId, 'clients');
    const snapshot = await getDocs(clientsRef);
    return snapshot.docs.map(doc => doc.data());
  } catch (error) {
    console.error('Error getting clients:', error);
    return [];
  }
}

export async function deleteClientFromFirestore(userId, clientId) {
  try {
    const clientRef = getUserDoc(userId, 'clients', clientId);
    await deleteDoc(clientRef);
  } catch (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
}

// ==================== SHIFTS ====================

export async function saveShiftToFirestore(userId, shift) {
  try {
    const shiftRef = getUserDoc(userId, 'shifts', shift.id);
    await setDoc(shiftRef, {
      ...shift,
      updatedAt: new Date().toISOString()
    });
    return shift;
  } catch (error) {
    console.error('Error saving shift:', error);
    throw error;
  }
}

export async function getShiftsFromFirestore(userId) {
  try {
    const shiftsRef = getUserCollection(userId, 'shifts');
    const snapshot = await getDocs(shiftsRef);
    return snapshot.docs.map(doc => doc.data());
  } catch (error) {
    console.error('Error getting shifts:', error);
    return [];
  }
}

export async function deleteShiftFromFirestore(userId, shiftId) {
  try {
    const shiftRef = getUserDoc(userId, 'shifts', shiftId);
    await deleteDoc(shiftRef);
  } catch (error) {
    console.error('Error deleting shift:', error);
    throw error;
  }
}

// ==================== INVOICES ====================

export async function saveInvoiceToFirestore(userId, invoice) {
  try {
    const invoiceRef = getUserDoc(userId, 'invoices', invoice.id);
    await setDoc(invoiceRef, {
      ...invoice,
      updatedAt: new Date().toISOString()
    });
    return invoice;
  } catch (error) {
    console.error('Error saving invoice:', error);
    throw error;
  }
}

export async function getInvoicesFromFirestore(userId) {
  try {
    const invoicesRef = getUserCollection(userId, 'invoices');
    const snapshot = await getDocs(invoicesRef);
    return snapshot.docs.map(doc => doc.data());
  } catch (error) {
    console.error('Error getting invoices:', error);
    return [];
  }
}

export async function deleteInvoiceFromFirestore(userId, invoiceId) {
  try {
    const invoiceRef = getUserDoc(userId, 'invoices', invoiceId);
    await deleteDoc(invoiceRef);
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
}

// ==================== SETTINGS ====================

export async function saveSettingsToFirestore(userId, settings) {
  try {
    const settingsRef = getUserDoc(userId, 'settings', 'app-settings');
    await setDoc(settingsRef, {
      ...settings,
      updatedAt: new Date().toISOString()
    });
    return settings;
  } catch (error) {
    console.error('Error saving settings:', error);
    throw error;
  }
}

export async function getSettingsFromFirestore(userId) {
  try {
    const settingsRef = getUserDoc(userId, 'settings', 'app-settings');
    const snapshot = await getDoc(settingsRef);
    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    console.error('Error getting settings:', error);
    return null;
  }
}

// ==================== SERVICE AGREEMENTS ====================

export async function saveServiceAgreementToFirestore(userId, agreement) {
  try {
    const agreementRef = getUserDoc(userId, 'serviceAgreements', agreement.id);
    await setDoc(agreementRef, {
      ...agreement,
      updatedAt: new Date().toISOString()
    });
    return agreement;
  } catch (error) {
    console.error('Error saving service agreement:', error);
    throw error;
  }
}

export async function getServiceAgreementsFromFirestore(userId) {
  try {
    const agreementsRef = getUserCollection(userId, 'serviceAgreements');
    const snapshot = await getDocs(agreementsRef);
    const agreements = snapshot.docs.map(doc => doc.data());
    // Sort client-side to avoid requiring a Firestore index
    return agreements.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting service agreements:', error);
    return [];
  }
}

export async function getServiceAgreementFromFirestore(userId, agreementId) {
  try {
    const agreementRef = getUserDoc(userId, 'serviceAgreements', agreementId);
    const snapshot = await getDoc(agreementRef);
    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    console.error('Error getting service agreement:', error);
    return null;
  }
}

export async function deleteServiceAgreementFromFirestore(userId, agreementId) {
  try {
    const agreementRef = getUserDoc(userId, 'serviceAgreements', agreementId);
    await deleteDoc(agreementRef);
  } catch (error) {
    console.error('Error deleting service agreement:', error);
    throw error;
  }
}

// ==================== RECURRING SCHEDULES ====================

export async function saveRecurringScheduleToFirestore(userId, schedule) {
  try {
    const scheduleRef = getUserDoc(userId, 'recurringSchedules', schedule.id);
    await setDoc(scheduleRef, {
      ...schedule,
      updatedAt: new Date().toISOString()
    });
    return schedule;
  } catch (error) {
    console.error('Error saving recurring schedule:', error);
    throw error;
  }
}

export async function getRecurringSchedulesFromFirestore(userId) {
  try {
    const schedulesRef = getUserCollection(userId, 'recurringSchedules');
    const snapshot = await getDocs(schedulesRef);
    return snapshot.docs.map(doc => doc.data());
  } catch (error) {
    console.error('Error getting recurring schedules:', error);
    return [];
  }
}

export async function deleteRecurringScheduleFromFirestore(userId, scheduleId) {
  try {
    const scheduleRef = getUserDoc(userId, 'recurringSchedules', scheduleId);
    await deleteDoc(scheduleRef);
  } catch (error) {
    console.error('Error deleting recurring schedule:', error);
    throw error;
  }
}

// ==================== SHIFT NOTES ====================

export async function saveShiftNoteToFirestore(userId, note) {
  try {
    const noteRef = getUserDoc(userId, 'shiftNotes', note.id);
    await setDoc(noteRef, {
      ...note,
      updatedAt: new Date().toISOString()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving shift note:', error);
    throw error;
  }
}

export async function getShiftNotesFromFirestore(userId) {
  try {
    const notesRef = getUserCollection(userId, 'shiftNotes');
    const snapshot = await getDocs(notesRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting shift notes:', error);
    return [];
  }
}

export async function deleteShiftNoteFromFirestore(userId, noteId) {
  try {
    const noteRef = getUserDoc(userId, 'shiftNotes', noteId);
    await deleteDoc(noteRef);
  } catch (error) {
    console.error('Error deleting shift note:', error);
    throw error;
  }
}

// ==================== BULK SYNC ====================

export async function syncAllDataToFirestore(userId) {
  try {
    // Get all data from localStorage
    const clients = JSON.parse(localStorage.getItem('ndis_clients') || '[]');
    const shifts = JSON.parse(localStorage.getItem('ndis_shifts') || '[]');
    const invoices = JSON.parse(localStorage.getItem('ndis_invoices') || '[]');
    const serviceAgreements = JSON.parse(localStorage.getItem('ndis_service_agreements') || '[]');
    const settings = JSON.parse(localStorage.getItem('ndis_settings') || '{}');

    // Upload all data
    const promises = [];

    // Clients
    clients.forEach(client => {
      promises.push(saveClientToFirestore(userId, client));
    });

    // Shifts
    shifts.forEach(shift => {
      promises.push(saveShiftToFirestore(userId, shift));
    });

    // Invoices
    invoices.forEach(invoice => {
      promises.push(saveInvoiceToFirestore(userId, invoice));
    });

    // Service Agreements
    serviceAgreements.forEach(agreement => {
      promises.push(saveServiceAgreementToFirestore(userId, agreement));
    });

    // Settings
    if (Object.keys(settings).length > 0) {
      promises.push(saveSettingsToFirestore(userId, settings));
    }

    await Promise.all(promises);

    return {
      clients: clients.length,
      shifts: shifts.length,
      invoices: invoices.length,
      serviceAgreements: serviceAgreements.length,
      settings: Object.keys(settings).length > 0 ? 1 : 0
    };
  } catch (error) {
    console.error('Error syncing data:', error);
    throw error;
  }
}

export async function loadAllDataFromFirestore(userId) {
  try {
    const [clients, shifts, invoices, serviceAgreements, settings, recurringSchedules] = await Promise.all([
      getClientsFromFirestore(userId),
      getShiftsFromFirestore(userId),
      getInvoicesFromFirestore(userId),
      getServiceAgreementsFromFirestore(userId),
      getSettingsFromFirestore(userId),
      getRecurringSchedulesFromFirestore(userId)
    ]);

    return { clients, shifts, invoices, serviceAgreements, settings, recurringSchedules };
  } catch (error) {
    console.error('Error loading data:', error);
    throw error;
  }
}
